import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

const ADMIN_TOKEN_KEY = 'admin_token';

export const adminService = {
  getToken(): string | null {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY);
  },

  setToken(token: string) {
    sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  },

  clearToken() {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  },

  async login(username: string, password: string) {
    const response = await api.post('/admin/login', { username, password });
    // If 2FA required, return the full response (no token yet)
    if (response.data.requires2FA) return response.data;
    this.setToken(response.data.token);
    return response.data;
  },

  async verify2FA(code: string, preAuthToken: string) {
    const response = await api.post('/admin/verify-2fa', { code, preAuthToken });
    this.setToken(response.data.token);
    return response.data;
  },

  async getFileAccessLogs(filters?: { fileKey?: string; email?: string }) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const params: Record<string, string> = {};
    if (filters?.fileKey) params.fileKey = filters.fileKey;
    if (filters?.email) params.email = filters.email;

    const response = await api.get('/admin/file-access-logs', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response.data;
  },

  async getUsers() {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const response = await api.get('/admin/users', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async updateUser(email: string, updates: Record<string, any>) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const response = await api.put('/admin/users', { email, updates }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async deleteUser(email: string) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const response = await api.delete('/admin/users', {
      headers: { Authorization: `Bearer ${token}` },
      data: { email },
    });
    return response.data;
  },

  async getSupportTickets(status?: string) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const params: Record<string, string> = {};
    if (status) params.status = status;

    const response = await api.get('/admin/support-tickets', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response.data;
  },

  async updateSupportTicket(email: string, caseNumber: string, updates: { status?: string; reply?: string }) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const response = await api.put('/admin/support-tickets', { email, caseNumber, ...updates }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getSpxConfig() {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const response = await api.get('/admin/spx-config', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async updateSpxConfig(updates: { minCredit: number }) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const response = await api.put('/admin/spx-config', updates, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // ---- trade-signals / iv-config (universe + option filter knobs) ----
  // GET returns {keys: {key: {value, updatedAt, updatedBy}}, schema: {key: {type,min,max,section,label}}}
  // PUT accepts a partial dict {key: value, ...}; server validates per-key.
  async getIvConfig() {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const response = await api.get('/admin/iv-config', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data as {
      keys: Record<string, { value: number | null; updatedAt: string | null; updatedBy: string | null }>;
      schema: Record<string, { type: 'int' | 'float' | 'bool01'; min?: number; max?: number; section: string; label: string }>;
    };
  },

  async updateIvConfig(updates: Record<string, number>) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const response = await api.put('/admin/iv-config', updates, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getSpxCosts(startDate?: string, endDate?: string) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get('/admin/spx-costs', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response.data;
  },

  // ─── Index Options ────────────────────────────────────────────────────────

  async getIndexConfig(symbol: string = 'SPY') {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const response = await api.get(`/admin/index-config?symbol=${symbol}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async updateIndexConfig(updates: { minCredit: number; symbol?: string }) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const response = await api.put('/admin/index-config', updates, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getIndexCosts(startDate?: string, endDate?: string) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get('/admin/index-costs', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response.data;
  },

  async getBillingAudit(filters?: { email?: string; event?: string; product?: string; startDate?: string; endDate?: string; limit?: number }) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');

    const params: Record<string, string> = {};
    if (filters?.email) params.email = filters.email;
    if (filters?.event) params.event = filters.event;
    if (filters?.product) params.product = filters.product;
    if (filters?.startDate) params.startDate = filters.startDate;
    if (filters?.endDate) params.endDate = filters.endDate;
    if (filters?.limit) params.limit = String(filters.limit);

    const response = await api.get('/admin/billing-audit', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response.data;
  },

  // ── Regime service (Phase 4-5 admin) ─────────────────────────────────

  async getRegimeState() {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const response = await api.get('/admin/regime/state', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getRegimeRecent(engine: string, days: number = 1) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const response = await api.get('/admin/regime/recent', {
      headers: { Authorization: `Bearer ${token}` },
      params: { engine, days: String(days) },
    });
    return response.data;
  },

  async updateRegimeEngine(engineId: string, updates: { regimeMode?: string; regimeWeight?: number }) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const response = await api.put('/admin/regime/engine', { engineId, ...updates }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // ── Calendar-Notches signal (Phase 1 admin) ──────────────────────────

  async getCalendarNotchesSummary() {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const response = await api.get('/admin/calendar-notches/summary', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async getCalendarNotchesLatest(symbol: string) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const response = await api.get('/admin/calendar-notches/latest', {
      headers: { Authorization: `Bearer ${token}` },
      params: { symbol },
    });
    return response.data;
  },

  async getCalendarNotchesHistory(symbol: string, limit: number = 48) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const response = await api.get('/admin/calendar-notches/history', {
      headers: { Authorization: `Bearer ${token}` },
      params: { symbol, limit: String(limit) },
    });
    return response.data;
  },

  // ── Briefing shadow review (Opus vs Sonnet dual-write comparison) ────

  async getBriefingShadow(days: number = 7) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const response = await api.get('/admin/briefings/shadow', {
      headers: { Authorization: `Bearer ${token}` },
      params: { days: String(days) },
    });
    return response.data;
  },

  // ── Study-page click stats ───────────────────────────────────────────

  async getPageViewStats(opts?: { days?: number; includeSmoke?: boolean }) {
    const token = this.getToken();
    if (!token) throw new Error('No admin token');
    const params: Record<string, string> = {};
    if (opts?.days) params.days = String(opts.days);
    if (opts?.includeSmoke) params.includeSmoke = 'true';
    const response = await api.get('/admin/page-view-stats', {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response.data;
  },
};
